import { useCallback, useEffect, useRef, useState } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import axios from 'axios';
import api from '../../../api';
import type { BrowserLadderSection, SectionPayloadOf } from './types';

/**
 * Loads one Browser Ladder section on its own. Every section is fetched
 * independently, so a slow or failing one only affects its own card and can
 * be retried alone; a stale response (older `weeks`) is dropped when a newer
 * request has since been issued.
 *
 * A cold section answers `202 { status: 'warming' }` while the server is
 * still computing it; the hook keeps polling (without the cache bypass) until
 * the cached payload is ready or `WARMING_MAX_POLLS` is exhausted.
 */

export type SectionErrorKind = 'timeout' | 'failed';

export interface SectionState<T> {
  data: T | null;
  loading: boolean;
  /** True while the server has said "warming" and we are polling for it. */
  warming: boolean;
  error: SectionErrorKind | null;
  /** Re-request the section, bypassing the server cache. */
  retry: () => void;
}

interface WarmingResponse {
  success: false;
  status: 'warming';
  retryAfterMs?: number;
}

export const WARMING_MAX_POLLS = 30;
const DEFAULT_RETRY_MS = 3_000;

export function isWarming(body: unknown): body is WarmingResponse {
  return (
    typeof body === 'object' &&
    body !== null &&
    (body as { status?: unknown }).status === 'warming'
  );
}

export function errorKind(err: unknown): SectionErrorKind {
  if (axios.isAxiosError(err)) {
    if (err.response?.status === 504 || err.code === 'ECONNABORTED')
      return 'timeout';
  }
  return 'failed';
}

const sleep = (ms: number) => new Promise((r) => setTimeout(r, ms));

class WarmingTimeoutError extends Error {}

export function useLadderSection<S extends BrowserLadderSection>(
  section: S,
  weeks: number,
): SectionState<SectionPayloadOf<S>> {
  const { getAccessTokenSilently } = useAuth0();
  const [data, setData] = useState<SectionPayloadOf<S> | null>(null);
  const [loading, setLoading] = useState(true);
  const [warming, setWarming] = useState(false);
  const [error, setError] = useState<SectionErrorKind | null>(null);
  const [attempt, setAttempt] = useState(0);
  const requestSeq = useRef(0);
  const forceRefresh = useRef(false);

  useEffect(() => {
    const seq = ++requestSeq.current;
    const refresh = forceRefresh.current;
    forceRefresh.current = false;
    const current = () => seq === requestSeq.current;
    const fetchOnce = async (bypassCache: boolean) => {
      const token = await getAccessTokenSilently();
      return api.get<SectionPayloadOf<S> | WarmingResponse>(
        `/platform-stats/browser-ladder?section=${section}&weeks=${weeks}${
          bypassCache ? '&refresh=1' : ''
        }`,
        { headers: { Authorization: `Bearer ${token}` } },
      );
    };
    const load = async () => {
      setLoading(true);
      setWarming(false);
      setError(null);
      try {
        let response = await fetchOnce(refresh);
        let polls = 0;
        while (current() && isWarming(response.data)) {
          if (++polls > WARMING_MAX_POLLS) throw new WarmingTimeoutError();
          setWarming(true);
          await sleep(response.data.retryAfterMs ?? DEFAULT_RETRY_MS);
          if (!current()) return;
          response = await fetchOnce(false);
        }
        if (!current() || isWarming(response.data)) return;
        setData(response.data);
      } catch (err) {
        if (!current()) return;
        setData(null);
        setError(
          err instanceof WarmingTimeoutError ? 'timeout' : errorKind(err),
        );
      } finally {
        if (current()) {
          setLoading(false);
          setWarming(false);
        }
      }
    };
    load();
    return () => {
      requestSeq.current++;
    };
  }, [section, weeks, attempt, getAccessTokenSilently]);

  const retry = useCallback(() => {
    forceRefresh.current = true;
    setAttempt((n) => n + 1);
  }, []);

  return { data, loading, warming, error, retry };
}
